import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { BASE_PATH, DYNAMIC_SEGMENT, PATH_ROOT_ID } from '@unifyapps/carbon/no-code/const';
import type { InterfacePathNode } from '@unifyapps/defs/types/interface';

export type InterfacePageParam = string | string[];

const SURROUNDING_SLASHES_REGEX = /^\/+|\/+$/g;

/** Convert a path param to a string path. e.g. `['foo', 'bar']` → `foo/bar`
 * and for /foo it will return foo, and for foo it will return foo
 */
export function getNormalizedPathFromSlugOrPathNames(slugOrPathNames: InterfacePageParam): string {
  const path = Array.isArray(slugOrPathNames) ? slugOrPathNames.join(BASE_PATH) : slugOrPathNames;

  return path.replace(SURROUNDING_SLASHES_REGEX, '');
}

/**
 * Get the path segments from a normalized path.
 * @param normalizedPath - the normalized path to get the segments from
 * @returns the path segments
 */
export function getPathSegmentsFromNormalizedPath(normalizedPath: string): string[] {
  return normalizedPath.split(BASE_PATH).filter(Boolean);
}

/**
 * @param normalizedPathOrSlug - given a normalized path, it splits the path and gives the last part as slug and the rest as path
 * @returns the slug and path
 */
export function getPageSlugAndPath(normalizedPathOrSlug: string): { slug: string; path?: string } {
  const normalizedPath = getNormalizedPathFromSlugOrPathNames(normalizedPathOrSlug);
  const segments = getPathSegmentsFromNormalizedPath(normalizedPath);
  const slug = segments[segments.length - 1] ?? '';
  const pathPrefix = segments.slice(0, -1).join(BASE_PATH);

  return {
    slug,
    path: pathPrefix,
  };
}

export function backfillInterfacePathTreeFromSlugMap({
  pageIdToSlugMap,
}: {
  pageIdToSlugMap?: Record<string, string | undefined>;
}): Record<string, InterfacePathNode> {
  const pageIds = Object.keys(pageIdToSlugMap ?? EMPTY_OBJECT);

  if (!pageIds.length) {
    return EMPTY_OBJECT as Record<string, InterfacePathNode>;
  }

  return {
    [PATH_ROOT_ID]: {
      id: PATH_ROOT_ID,
      segment: '',
      parentId: null,
      children: pageIds,
      path: BASE_PATH,
      redirect: null,
    },
  };
}

export function getStaticInterfacePages<T extends { id: string }>({
  interfacePages,
  pathNodes,
}: {
  interfacePages: T[];
  pathNodes?: Record<string, InterfacePathNode>;
}): T[] {
  if (!pathNodes) {
    return interfacePages;
  }

  const excludedPageIds = new Set<string>();

  for (const node of Object.values(pathNodes)) {
    if (node.path.includes(DYNAMIC_SEGMENT) && node.children) {
      for (const childId of node.children) {
        if (!(childId in pathNodes)) {
          excludedPageIds.add(childId);
        }
      }
    }
  }

  return interfacePages.filter((page) => !excludedPageIds.has(page.id));
}
